import type { FileMetadata } from "./FileEntry";

/**
 * Standard file system events that implementations can emit
 */
export const FileSystemEvents = {
  // Initialization events
  INITIALIZING: "fs:initializing",
  INITIALIZED: "fs:initialized",
  INIT_PROGRESS: "fs:init:progress",
  INIT_ERROR: "fs:init:error",

  // Operation lifecycle events
  OPERATION_START: "fs:operation:start",
  OPERATION_END: "fs:operation:end",
  OPERATION_ERROR: "fs:operation:error",

  // File events (complement to watch)
  FILE_READING: "fs:file:reading",
  FILE_READ: "fs:file:read",
  FILE_WRITING: "fs:file:writing",
  FILE_WRITTEN: "fs:file:written",
  FILE_DELETING: "fs:file:deleting",
  FILE_DELETED: "fs:file:deleted",

  // Directory events
  DIR_READING: "fs:dir:reading",
  DIR_READ: "fs:dir:read",
  DIR_CREATING: "fs:dir:creating",
  DIR_CREATED: "fs:dir:created",
  DIR_DELETING: "fs:dir:deleting",
  DIR_DELETED: "fs:dir:deleted",

  // Batch operations
  BATCH_START: "fs:batch:start",
  BATCH_PROGRESS: "fs:batch:progress",
  BATCH_END: "fs:batch:end",

  // Storage events
  STORAGE_CLEARING: "fs:storage:clearing",
  STORAGE_CLEARED: "fs:storage:cleared",
  STORAGE_SIZE_CALCULATED: "fs:storage:size",
} as const;

/**
 * Event payload types
 */
export interface FileSystemEventPayloads {
  [FileSystemEvents.INITIALIZING]: void;
  [FileSystemEvents.INITIALIZED]: { duration: number };
  [FileSystemEvents.INIT_PROGRESS]: {
    loaded: number;
    total: number;
    phase?: string;
  };
  [FileSystemEvents.INIT_ERROR]: { error: Error };

  [FileSystemEvents.OPERATION_START]: {
    operation: string;
    path?: string;
    id?: string;
  };
  [FileSystemEvents.OPERATION_END]: {
    operation: string;
    path?: string;
    id?: string;
    duration: number;
  };
  [FileSystemEvents.OPERATION_ERROR]: {
    operation: string;
    path?: string;
    error: Error;
  };

  [FileSystemEvents.FILE_READING]: { path: string };
  [FileSystemEvents.FILE_READ]: { path: string; size: number };
  [FileSystemEvents.FILE_WRITING]: { path: string; size?: number };
  [FileSystemEvents.FILE_WRITTEN]: { path: string; size: number };
  [FileSystemEvents.FILE_DELETING]: { path: string };
  [FileSystemEvents.FILE_DELETED]: { path: string };

  [FileSystemEvents.DIR_READING]: { path: string };
  [FileSystemEvents.DIR_READ]: { path: string; count: number };
  [FileSystemEvents.DIR_CREATING]: { path: string; recursive: boolean };
  [FileSystemEvents.DIR_CREATED]: { path: string };
  [FileSystemEvents.DIR_DELETING]: { path: string; recursive: boolean };
  [FileSystemEvents.DIR_DELETED]: { path: string };

  [FileSystemEvents.BATCH_START]: { total: number; operation: string };
  [FileSystemEvents.BATCH_PROGRESS]: { current: number; total: number };
  [FileSystemEvents.BATCH_END]: { total: number; duration: number };

  [FileSystemEvents.STORAGE_CLEARING]: void;
  [FileSystemEvents.STORAGE_CLEARED]: { duration: number };
  [FileSystemEvents.STORAGE_SIZE_CALCULATED]: { size: number };
}

/**
 * File system event emitter type
 */
export type FileSystemEventEmitter =
  import("../interfaces/IEventEmitter").ITypedEventEmitter<FileSystemEventPayloads>;
